/* eslint-disable no-nested-ternary */
import TabsPanel from "@Components/Tabs/TabsPanel";
import { getSearchLink } from "@Pages/Search/data/helper";
import Chip from "../Chip";
import styles from "./panel.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import Chevron from "@Icons/Chevron";
import config from "@Config/index";

type PanelTypes = {
  name: string;
  data?: Array<any>;
  activeTab?: string;
  isDesktop?: boolean;
  children?: any;
  dataTestId?: string;
  renderAllPanels?: boolean;
  headingText?: string;
};

const Panel = ({
  name,
  data = [],
  isDesktop = false,
  children,
  dataTestId,
  renderAllPanels = false,
  headingText,
  ...rest
}: PanelTypes) => {
  const { t } = useTranslationHook();
  const INITIAL_VIEW_COUNT = !isDesktop ? 9 : config.IS_CHINA_APP ? 10 : 8;
  const shouldShowShowMore = (data?.length || 0) > INITIAL_VIEW_COUNT;
  const isEqual = (a: any, b: string) => a?.toLowerCase() === b?.toLowerCase();

  const isCities = isEqual(name, t("searchpage.components.nearbysection.citiestab"));
  const isUniversities = isEqual(name, t("searchpage.components.nearbysection.universitiestab"));
  const isInventories = isEqual(name, t("searchpage.components.nearbysection.propertiespanel"));
  const isLocality = isEqual(name, t("searchpage.components.nearbysection.areaspanel"));

  const activeTab = rest?.activeTab || "";
  const isActive = isEqual(activeTab, name);

  const checkIfInventory = () => (isInventories ? "inventory" : "");
  const uniqueId = `showMore-${name}`;

  return (
    <TabsPanel value={name} activeTab={activeTab} renderAllPanels={renderAllPanels}>
      {children ? (
        <>{children}</>
      ) : (
        <div className={!isActive && styles.hiddenPanel}>
          {headingText && <h3 className={styles.panelHeading}>{headingText}</h3>}
          <div className={styles.showMoreLessContainer}>
            <input
              type="checkbox"
              id={uniqueId}
              className={styles.showMoreCheckbox}
              data-testid={`${dataTestId}-showMore-checkbox`}
            />

            <div
              className={formatClassNames(
                styles.chipContainer,
                isDesktop
                  ? (isCities || isLocality) && config.IS_CHINA_APP
                    ? styles.chipContainerDesktopCities
                    : styles.chipContainerDesktop
                  : "",

                isDesktop && isUniversities
                  ? config.IS_CHINA_APP
                    ? styles.universitiesContainerChina
                    : styles.universitiesContainer
                  : "",
              )}
            >
              {data?.slice(0, INITIAL_VIEW_COUNT).map((item, idx) => (
                <div key={`initial-${idx}`} className={styles.visibleChip}>
                  <Chip
                    value={item.name}
                    canonical_name={item.canonical_name}
                    href={getSearchLink(item.canonical_name, {}, checkIfInventory())}
                    dataTestId={`${dataTestId}-visible-${idx}`}
                    groupName={name}
                  />
                </div>
              ))}

              <div className={styles.additionalChipsContainer}>
                {data?.slice(INITIAL_VIEW_COUNT).map((item, idx) => (
                  <div key={`additional-${idx}`} className={styles.additionalChip}>
                    <Chip
                      value={item.name}
                      canonical_name={item.canonical_name}
                      href={getSearchLink(item.canonical_name, {}, checkIfInventory())}
                      dataTestId={`${dataTestId}-hidden-${idx}`}
                      groupName={name}
                    />
                  </div>
                ))}
              </div>
            </div>

            {shouldShowShowMore && (
              <>
                <div className={styles.divider} />

                <label
                  htmlFor={uniqueId}
                  className={styles.showMoreLabel}
                  data-testid={`${dataTestId}-showMore-label`}
                >
                  <span className={styles.showMoreText}>
                    {t("searchpage.components.nearbysection.panel.showmore")} (
                    {data.length - INITIAL_VIEW_COUNT})
                    <Chevron height="12" width="12" />
                  </span>
                </label>

                <label
                  htmlFor={uniqueId}
                  className={styles.showLessLabel}
                  data-testid={`${dataTestId}-showLess-label`}
                >
                  <span className={styles.showLessText}>
                    {t("searchpage.components.nearbysection.panel.showless")}
                    <Chevron height="12" width="12" rotate="180" />
                  </span>
                </label>
              </>
            )}
          </div>
        </div>
      )}
    </TabsPanel>
  );
};

export default wrapperHOC(Panel, {
  componentName: "Panel-NearbySection-SearchPage",
  showForChina: true,
});
